import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { invoicesAPI, clientsAPI, quotesAPI, inventoryAPI, accountStatusesAPI, taxesAPI, hrAPI } from '../lib/api';
import CatalogItemPicker, { EMPTY_ITEM as CATALOG_EMPTY_ITEM } from '../components/CatalogItemPicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '../components/ui/command';
import { Plus, Search, Edit, Trash2, X, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { numberToWords } from '../utils/numberToWords';
import { useCurrency } from '../contexts/CurrencyContext';
import { TableSkeleton } from '../components/ui/loading-states';
import { EmptyState, SearchEmptyState } from '../components/ui/empty-states';
import { ErrorState } from '../components/ui/error-states';
import { DataPagination } from '../components/ui/data-pagination';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

// Predefined tax rates
const TAX_PRESETS = {
  india: [
    { name: 'CGST 9%', rate: 9 },
    { name: 'SGST 9%', rate: 9 },
    { name: 'IGST 18%', rate: 18 },
    { name: 'GST 5%', rate: 5 },
    { name: 'GST 12%', rate: 12 },
    { name: 'GST 28%', rate: 28 },
  ],
  usa: [
    { name: 'Sales Tax 6%', rate: 6 },
    { name: 'Sales Tax 7%', rate: 7 },
    { name: 'Sales Tax 8%', rate: 8 },
    { name: 'Sales Tax 9%', rate: 9 },
    { name: 'Sales Tax 10%', rate: 10 },
  ],
};

// Inventory items will be loaded from database

export default function Invoices() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const currency = useCurrency();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [inventoryItemsFromDB, setInventoryItemsFromDB] = useState([]);
  const [invoiceStatuses, setInvoiceStatuses] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientComboboxOpen, setClientComboboxOpen] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Removed dummy clients - only use real database data
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  
  const [formData, setFormData] = useState({
    client_id: '',
    quote_id: '',
    invoice_number: '',
    invoice_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: '',
    status: 'draft',
    discount_type: 'percentage',
    discount_value: 0,
    tax_jurisdiction: 'usa',
    tax_rate: 0,
    tax_name: '',
    instructions: '',
    notes: '',
    terms: '',
  });
  
  const [unlinkedQuotes, setUnlinkedQuotes] = useState([]);
  
  const [lineItems, setLineItems] = useState([CATALOG_EMPTY_ITEM()]);
  
  // (inventoryItems removed — unified into catalog-based lineItems)

  // Permission check functions
  const canUpdate = () => {
    if (!user?.module_permissions) return true; // Admin has all permissions
    return hasPermission('account', 'invoice', 'update');
  };

  const canDelete = () => {
    if (!user?.module_permissions) return true; // Admin has all permissions
    return hasPermission('account', 'invoice', 'delete');
  };

  useEffect(() => {
    loadData();
  }, [searchTerm, statusFilter, dateFrom, dateTo, currentPage, pageSize]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query params with pagination
      const skip = (currentPage - 1) * pageSize;
      const params = { limit: pageSize, skip };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      
      const [invoicesRes, countRes, clientsRes, inventoryRes, statusesRes, taxesRes] = await Promise.all([
        invoicesAPI.getAll(params),
        invoicesAPI.getCount({ search: searchTerm, status: statusFilter }),
        clientsAPI.getAll(),  // Get all clients (admin sees all, team sees own + admin's)
        inventoryAPI.getItems({}),  // All inventory items (admin + user)
        accountStatusesAPI.getInvoiceStatuses().catch(err => ({ data: [] })), // Load invoice statuses
        taxesAPI.getAll().catch(err => ({ data: [] })) // Load taxes
      ]);
      
      // Filter by date range on frontend (if backend doesn't support it)
      let filteredInvoices = invoicesRes.data || [];
      if (dateFrom || dateTo) {
        filteredInvoices = filteredInvoices.filter(invoice => {
          const invoiceDate = new Date(invoice.invoice_date);
          if (dateFrom && invoiceDate < new Date(dateFrom)) return false;
          if (dateTo && invoiceDate > new Date(dateTo)) return false;
          return true;
        });
      }
      
      setInvoices(filteredInvoices);
      setTotalCount(countRes.data?.count || 0);
      
      // Use only real clients from database
      const fetchedClients = clientsRes.data || [];
      // Sort clients by newest first (updated_at or created_at)
      fetchedClients.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;  // Descending order (newest first)
      });
      setClients(fetchedClients);
      
      // Use only real inventory items from database
      const fetchedInventory = inventoryRes.data || [];
      setInventoryItemsFromDB(fetchedInventory);
      
      // Set invoice statuses from database
      const statusesData = statusesRes.data || [];
      setInvoiceStatuses(statusesData);
      
      // Set taxes from database
      const taxesData = taxesRes.data?.items || taxesRes.data || [];
      setTaxes(taxesData);
      
      // Load team members for Created By column
      try {
        const teamRes = await hrAPI.getTeamMembers();
        setTeamMembers(teamRes.data || []);
      } catch (err) {
        console.error('Failed to load team members:', err);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err.message || 'Failed to load invoices');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getCreatorName = (createdBy) => {
    const member = teamMembers.find(m => m.id === createdBy);
    return member ? member.name : 'Unknown';
  };

  // Load unlinked quotes when client is selected
  const loadUnlinkedQuotes = async (clientId) => {
    if (!clientId) {
      setUnlinkedQuotes([]);
      return;
    }
    try {
      const response = await invoicesAPI.getUnlinkedQuotes(clientId);
      setUnlinkedQuotes(response.data || []);
    } catch (error) {
      console.error('Failed to load unlinked quotes:', error);
      setUnlinkedQuotes([]);
    }
  };

  const handleQuoteSelection = async (quoteId) => {
    if (!quoteId || quoteId === "none") {
      return;
    }
    
    try {
      const response = await quotesAPI.getOne(quoteId);
      const quote = response.data;
      
      // Populate form with quote data
      setFormData(prev => ({
        ...prev,
        discount_type: quote.discount_type || 'percentage',
        discount_value: quote.discount_value || 0,
        tax_jurisdiction: quote.tax_jurisdiction || 'usa',
        tax_rate: quote.tax_rate || 0,
        tax_name: quote.tax_name || '',
        instructions: quote.instructions || '',
        notes: quote.notes || '',
        terms: quote.terms || '',
      }));
      
      // Set line items from quote (convert to unified catalog picker format)
      if (quote.line_items && quote.line_items.length > 0) {
        const existingLineItems = quote.line_items.map(it => ({
          _key: Date.now() + Math.random(),
          catalog_item_id: it.catalog_item_id || null,
          description: it.description || '',
          quantity: it.quantity || 1,
          price: it.price || 0,
          taxRate: it.taxRate || it.tax_rate || 0,
        }));
        const existingInvItems = (quote.inventory_items || []).map(it => ({
          _key: Date.now() + Math.random(),
          catalog_item_id: it.itemId || it.catalog_item_id || null,
          description: it.name || it.description || '',
          quantity: it.quantity || 1,
          price: it.price || 0,
          taxRate: it.taxRate || it.tax_rate || 0,
        }));
        const merged = [...existingLineItems, ...existingInvItems];
        setLineItems(merged.length > 0 ? merged : [CATALOG_EMPTY_ITEM()]);
      }
      
      toast.success('Quote details loaded successfully');
    } catch (error) {
      console.error('Failed to load quote:', error);
      toast.error('Failed to load quote details');
    }
  };

  // Calculate Totals (from unified catalog-based line items)
  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0);
  };

  const calculateTax = () => {
    const itemTax = lineItems.reduce((sum, item) => {
      const subtotal = (item.quantity || 0) * (item.price || 0);
      return sum + subtotal * ((item.taxRate || 0) / 100);
    }, 0);
    const subtotal = calculateSubtotal();
    const additionalTax = subtotal * (formData.tax_rate || 0) / 100;
    return itemTax + additionalTax;
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    if (formData.discount_type === 'percentage') {
      return subtotal * (formData.discount_value || 0) / 100;
    }
    return formData.discount_value || 0;
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    const discount = calculateDiscount();
    return subtotal + tax - discount;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }

    const grandTotal = calculateGrandTotal();
    const invoiceData = {
      ...formData,
      // Convert empty date strings to null for backend validation
      due_date: formData.due_date || null,
      line_items: lineItems.filter(it => it.description?.trim() || it.catalog_item_id).map(it => ({
        catalog_item_id: it.catalog_item_id || null,
        description: it.description,
        quantity: it.quantity || 1,
        price: it.price || 0,
        tax: ((it.quantity || 0) * (it.price || 0)) * ((it.taxRate || 0) / 100),
        taxRate: it.taxRate || 0,
        total: ((it.quantity || 0) * (it.price || 0)) * (1 + (it.taxRate || 0) / 100),
      })),
      inventory_items: [],
      subtotal: calculateSubtotal(),
      tax_amount: calculateTax(),
      discount_amount: calculateDiscount(),
      total_amount: grandTotal,
      total_in_words: numberToWords(grandTotal, currency.currencyCode)
    };

    try {
      if (editingInvoice) {
        await invoicesAPI.update(editingInvoice.id, invoiceData);
        toast.success('Invoice updated successfully');
        setSheetOpen(false);
        resetForm();
        loadData();
      } else {
        const response = await invoicesAPI.create(invoiceData);
        toast.success('Invoice created successfully');
        setSheetOpen(false);
        resetForm();
        // Navigate to invoice details page
        navigate(`/invoices/${response.data.id}`);
      }
    } catch (error) {
      toast.error('Failed to save invoice');
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      client_id: invoice.client_id || '',
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date ? format(new Date(invoice.invoice_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      due_date: invoice.due_date ? format(new Date(invoice.due_date), 'yyyy-MM-dd') : '',
      status: invoice.status || 'draft',
      discount_type: invoice.discount_type || 'percentage',
      discount_value: invoice.discount_value || 0,
      tax_jurisdiction: invoice.tax_jurisdiction || 'usa',
      tax_rate: invoice.tax_rate || 0,
      tax_name: invoice.tax_name || '',
      deposit_required: invoice.deposit_required || 0,
      instructions: invoice.instructions || '',
      notes: invoice.notes || '',
      terms: invoice.terms || '',
    });
    // Convert existing line items + inventory items into unified catalog picker format
    const existingLineItems = (invoice.line_items || []).map(it => ({
      _key: Date.now() + Math.random(),
      catalog_item_id: it.catalog_item_id || null,
      description: it.description || '',
      quantity: it.quantity || 1,
      price: it.price || 0,
      taxRate: it.taxRate || it.tax_rate || 0,
    }));
    const existingInvItems = (invoice.inventory_items || []).map(it => ({
      _key: Date.now() + Math.random(),
      catalog_item_id: it.itemId || it.catalog_item_id || null,
      description: it.name || it.description || '',
      quantity: it.quantity || 1,
      price: it.price || 0,
      taxRate: it.taxRate || it.tax_rate || 0,
    }));
    const merged = [...existingLineItems, ...existingInvItems];
    setLineItems(merged.length > 0 ? merged : [CATALOG_EMPTY_ITEM()]);
    setSheetOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await invoicesAPI.delete(id);
        toast.success('Invoice deleted successfully');
        loadData();
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Failed to delete invoice';
        toast.error(errorMessage);
      }
    }
  };

  const resetForm = () => {
    setEditingInvoice(null);
    setFormData({
      client_id: '',
      invoice_number: '',
      invoice_date: format(new Date(), 'yyyy-MM-dd'),
      due_date: '',
      status: 'draft',
      discount_type: 'percentage',
      discount_value: 0,
      tax_jurisdiction: 'usa',
      tax_rate: 0,
      tax_name: '',
      deposit_required: 0,
      instructions: '',
      notes: '',
      terms: '',
    });
    setLineItems([CATALOG_EMPTY_ITEM()]);
    setClientSearch('');
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.email?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.company_name?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const subtotal = calculateSubtotal();
  const tax = calculateTax();
  const discount = calculateDiscount();
  const grandTotal = calculateGrandTotal();

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
          <p className="text-muted-foreground">Manage and track your sales invoices</p>
        </div>
        {hasPermission('account', 'invoice', 'create') && (
          <Sheet open={sheetOpen} onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) resetForm();
          }}>
            <SheetTrigger asChild>
              <Button data-testid="create-invoice-button">
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-6xl overflow-y-auto" data-testid="invoice-sheet">
            <SheetHeader>
              <SheetTitle>{editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}</SheetTitle>
            </SheetHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label>Client *</Label>
                    <Popover open={clientComboboxOpen && !editingInvoice} onOpenChange={!editingInvoice ? setClientComboboxOpen : undefined}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={clientComboboxOpen}
                          className="w-full justify-between"
                          data-testid="client-select"
                          disabled={!!editingInvoice}
                        >
                          {formData.client_id
                            ? clients.find((client) => client.id === formData.client_id)?.name || "Select client"
                            : "Search and select client..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0">
                        <Command>
                          <CommandInput placeholder="Search clients..." data-testid="client-search-input" />
                          <CommandEmpty>No client found.</CommandEmpty>
                          <CommandGroup className="max-h-64 overflow-auto">
                            {clients.map((client) => (
                              <CommandItem
                                key={client.id}
                                value={`${client.name} ${client.email} ${client.company_name || ''}`}
                                onSelect={() => {
                                  setFormData({ ...formData, client_id: client.id, quote_id: '' });
                                  loadUnlinkedQuotes(client.id);
                                  setClientComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    formData.client_id === client.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{client.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {client.company_name && `${client.company_name} • `}{client.email}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Link to Quote (Optional)</Label>
                    <Select
                      value={formData.quote_id || "none"}
                      onValueChange={(value) => {
                        const quoteId = value === "none" ? "" : value;
                        setFormData({ ...formData, quote_id: quoteId });
                        if (quoteId) {
                          handleQuoteSelection(quoteId);
                        }
                      }}
                      disabled={!formData.client_id}
                    >
                      <SelectTrigger data-testid="quote-select">
                        <SelectValue placeholder={formData.client_id ? "Select a quote" : "Select client first"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Quote</SelectItem>
                        {unlinkedQuotes.map((quote) => (
                          <SelectItem key={quote.id} value={quote.id}>
                            {quote.quote_number} - {currency.format(parseFloat(quote.total_amount || 0))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Invoice Number</Label>
                    <Input
                      placeholder="Auto-generated if empty"
                      value={formData.invoice_number}
                      onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                      data-testid="invoice-number-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Invoice Date *</Label>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                      required
                      data-testid="invoice-date-input"
                    />
                  </div>
                  <div>
                    <Label>Payment Due Date</Label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      data-testid="expiry-date-input"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {invoiceStatuses.map((status) => (
                          <SelectItem key={status.id} value={status.name}>{status.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Line Items — unified catalog picker */}
              <CatalogItemPicker lineItems={lineItems} setLineItems={setLineItems} currency={currency} />

              {/* Taxes and Discounts */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Taxes & Discounts</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tax</Label>
                    <Select
                      value={`${formData.tax_rate}`}
                      onValueChange={(value) => {
                        const selectedTax = taxes.find(t => t.rate.toString() === value);
                        setFormData({ 
                          ...formData, 
                          tax_rate: selectedTax ? parseFloat(selectedTax.rate) : 0,
                          tax_name: selectedTax ? selectedTax.name : ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="tax-select">
                        <SelectValue placeholder="Select tax" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No Tax</SelectItem>
                        {taxes.map((tax) => (
                          <SelectItem key={tax.id} value={tax.rate.toString()}>
                            {tax.name} ({tax.rate}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <Select value={formData.discount_type} onValueChange={(value) => setFormData({ ...formData, discount_type: value })}>
                      <SelectTrigger data-testid="discount-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Discount Value</Label>
                    <Input
                      type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      data-testid="discount-value-input"
                    />
                  </div>
                  <div>
                    <Label>Required Deposit ($)</Label>
                    <Input
                      type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                      value={formData.deposit_required}
                      onChange={(e) => setFormData({ ...formData, deposit_required: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      data-testid="deposit-input"
                    />
                  </div>
                </div>
              </div>

              {/* Totals Summary */}
              {lineItems.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span className="font-medium">{currency.format(subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax ({formData.tax_name || 'N/A'}):</span>
                        <span className="font-medium">{currency.format(tax)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Discount ({formData.discount_type === 'percentage' ? `${formData.discount_value}%` : '$' + formData.discount_value}):
                        </span>
                        <span className="font-medium text-green-600">-{currency.format(discount)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="text-lg font-bold">Grand Total:</span>
                        <span className="text-lg font-bold text-primary">{currency.format(grandTotal)}</span>
                      </div>
                      <div className="pt-2 border-t">
                        <p className="text-sm text-muted-foreground">Amount in Words:</p>
                        <p className="text-sm font-medium italic">{numberToWords(grandTotal, currency.currencyCode)}</p>
                      </div>
                      {formData.deposit_required > 0 && (
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-sm font-medium">Required Deposit:</span>
                          <span className="text-sm font-bold text-orange-600">{currency.format(formData.deposit_required)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Instructions and Notes */}
              <div className="space-y-4">
                <div>
                  <Label>Instructions (Customer-facing)</Label>
                  <Textarea
                    placeholder="Special instructions for the client..."
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    rows={3}
                    data-testid="instructions-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Internal Notes</Label>
                    <Textarea
                      placeholder="Internal notes (not visible to client)..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                      data-testid="notes-input"
                    />
                  </div>
                  <div>
                    <Label>Terms & Conditions</Label>
                    <Textarea
                      placeholder="Payment terms, delivery terms, etc..."
                      value={formData.terms}
                      onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                      rows={2}
                      data-testid="terms-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setSheetOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-invoice-button">
                  {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>
        )}
      </div>

      {/* Filters Section */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label>Search</Label>
              <Input
                placeholder="Search by invoice number..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page when searching
                }}
                data-testid="invoice-search-input"
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter || "all"} onValueChange={(value) => {
                setStatusFilter(value === "all" ? "" : value);
                setCurrentPage(1); // Reset to first page when filtering
              }}>
                <SelectTrigger data-testid="status-filter-select">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {invoiceStatuses.map((status) => (
                    <SelectItem key={status.id} value={status.name}>{status.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from-input"
              />
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to-input"
              />
            </div>
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || statusFilter || dateFrom || dateTo) && (
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setDateFrom('');
                  setDateTo('');
                }}
                data-testid="clear-filters-button"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          
          {/* Loading State */}
          {loading && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <TableSkeleton rows={8} columns={8} />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          
          {/* Data Table Body */}
          {!loading && !error && (
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="p-0">
                    {searchTerm || statusFilter || dateFrom || dateTo ? (
                      <SearchEmptyState 
                        searchTerm={searchTerm} 
                        onClearSearch={() => {
                          setSearchTerm('');
                          setStatusFilter('');
                          setDateFrom('');
                          setDateTo('');
                        }} 
                      />
                    ) : (
                      <EmptyState
                        title="No invoices yet"
                        description="Create your first invoice to start tracking payments from clients."
                        icon="invoices"
                        actionLabel={hasPermission('account', 'invoice', 'create') ? "Create Invoice" : null}
                        onAction={hasPermission('account', 'invoice', 'create') ? () => setSheetOpen(true) : null}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                  <TableCell className="font-medium">
                    <button
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      className="text-primary hover:underline focus:outline-none"
                      data-testid={`invoice-number-${invoice.id}`}
                    >
                      {invoice.invoice_number || invoice.id.slice(0, 8)}
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => invoice.client_id && navigate(`/clients/${invoice.client_id}`)}
                      className="text-primary hover:underline focus:outline-none"
                      data-testid={`client-name-${invoice.id}`}
                    >
                      {invoice.client_name || invoice.client_id || 'Unknown Client'}
                    </button>
                  </TableCell>
                  <TableCell>
                    {invoice.invoice_date ? format(new Date(invoice.invoice_date), 'MMM dd, yyyy') : '-'}
                  </TableCell>
                  <TableCell>
                    {invoice.due_date ? format(new Date(invoice.due_date), 'MMM dd, yyyy') : '-'}
                  </TableCell>
                  <TableCell className="font-semibold">
                    {currency.format(parseFloat(invoice.total_amount || 0))}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      style={{
                        backgroundColor: invoiceStatuses.find(s => s.name.toLowerCase() === invoice.status?.toLowerCase())?.color || '#gray',
                        color: '#fff'
                      }}
                    >
                      {invoiceStatuses.find(s => s.name.toLowerCase() === invoice.status?.toLowerCase())?.name || 
                       invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {invoice.created_by_name || 'Unknown'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canUpdate() && invoice.status?.toLowerCase() !== 'paid' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(invoice)}
                          data-testid={`edit-invoice-${invoice.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete() && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(invoice.id)}
                          data-testid={`delete-invoice-${invoice.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
              )}
            </TableBody>
          )}
        </Table>
        
        {/* Pagination */}
        {!loading && !error && invoices.length > 0 && (
          <DataPagination
            currentPage={currentPage}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
        
        {/* Error State - shown outside table */}
        {!loading && error && (
          <ErrorState
            title="Failed to load invoices"
            message={error}
            type="server"
            onRetry={loadData}
          />
        )}
      </div>
    </div>
  );
}
